import React, { useState } from 'react';
import { Typography, Box, IconButton } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import { getShareLightSubmission, updateLikeCount, updateShareCount } from 'pages/ShareYourLight/api';
import { handleApiError, trackMixpanelEvent } from 'utils/utilMethods';
import { useMutation, useQuery } from 'react-query';
import SYLButton from 'pages/ShareYourLight/components/SYLButton';
import CommonModal from 'pages/ShareYourLight/components/CommonModal';
import http from 'utils/http';
import { MixpanelEventName } from 'utils/constants';


interface ThoughtViewModalProps {
  open: boolean;
  onClose: () => void;
  thoughtId: string;
  setAddMode?: React.Dispatch<React.SetStateAction<boolean>>;
  liked: boolean;
  setLiked: (val: boolean) => void;
  csrfToken: string;
  refetchCsrfToken: () => Promise<string | undefined>;
}

function ThoughtViewModal({
  open,
  onClose,
  thoughtId,
  setAddMode,
  liked,
  setLiked,
  csrfToken,
  refetchCsrfToken,

}: ThoughtViewModalProps) {
  const [likeCount, setLikeCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const { data: thought } = useQuery({
    queryKey: ['thought', thoughtId],
    queryFn: getShareLightSubmission(http.get),
    enabled: !!thoughtId && open,
    onSuccess: (data) => {
      setLikeCount(data?.submission.likes ?? 0);
      setShareCount(data?.submission.shares ?? 0);
    },
    onError: (err) => {
      handleApiError(err, 'ThoughtViewModal: Failed to fetch thought');
    },
  });

  const updateLikesMutation = useMutation({
    mutationFn: async (isLiked: string) => {
      const tryUpdateLikeCount = async (token: string, attempts: number): Promise<void> => {
        try {
          return await updateLikeCount(http.post)(
            thoughtId,
            isLiked === 'true' ? 'LIKED' : 'DISLIKED',
            token,
          );
        }
        catch (err) {
          if (err instanceof Error && ['CSRF token is invalid', 'missing CSRF token'].includes(err.message) && attempts < 1) {
            const newToken = await refetchCsrfToken() || token;
            return tryUpdateLikeCount(newToken, attempts + 1);
          }
          throw err;
        }
      };
      return tryUpdateLikeCount(csrfToken, 0);
    },
    onError: (err) => {
      handleApiError(err, 'ThoughtViewModal: Failed to update like count');
    },
  });

  const updateShareMutation = useMutation({
    mutationFn: async () => {
      const tryUpdateShareCount = async (token: string, attempts: number): Promise<void> => {
        try {
          return await updateShareCount(http.post)(
            thoughtId,
            token,
          );
        }
        catch (err) {
          if (err instanceof Error && ['CSRF token is invalid', 'missing CSRF token'].includes(err.message) && attempts < 1) {
            const newToken = await refetchCsrfToken() || token;
            return tryUpdateShareCount(newToken, attempts + 1);
          }
          throw err;
        }
      };
      return tryUpdateShareCount(csrfToken, 0);
    },
    onError: (err) => {
      handleApiError(err, 'ThoughtViewModal: Failed to update share count');
    },
  });

  const handleLike = () => {
    if (!thoughtId) return;
    if (!liked) {
      setLiked(true);
      setLikeCount((prev) => prev + 1);
      updateLikesMutation.mutate('true');
    }
    else {
      setLiked(false);
      setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
      updateLikesMutation.mutate('false');
    }
  };

  const handleCopy = () => {
    // Copy URL with ?thoughtId= param
    const url = new URL(window.location.href);
    url.searchParams.set('thoughtId', thoughtId);
    navigator.clipboard.writeText(url.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
    setShareCount((prev) => prev + 1);
    updateShareMutation.mutate();
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      paperSx={{ maxWidth: 540 }}
      title={(
        <Typography variant='h6' sx={{ color: '#FED892', fontWeight: 500, mb: 2, fontSize: 20, textAlign: 'left' }}>
          {thought?.submission?.title}
        </Typography>
      )}
      avoidLeftTitleMargin
    >

      <Box sx={{
        background: '#25282F',
        borderRadius: 2,
        p: 1.5,
        mb: 3,
      }}
      >
        <Box className='fs-mask'>
          <Typography
            sx={{
              color: '#fff',
              fontSize: 18,
              fontWeight: 400,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
              overflowWrap: 'break-word',
              overflow: 'auto',
            }}
          >
            {thought?.submission.content}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.8, mt: 1 }} flexWrap='wrap'>
          <Typography fontSize={18} color='#fff' sx={{ fontWeight: 500 }} fontStyle='italic' mr='auto' whiteSpace='nowrap'>
            -
            {' '}
            {thought?.submission.name ?? 'Anonymous'}
          </Typography>
          <Box display='flex' alignItems='center'>
            <IconButton
              onClick={() => {
                trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_LIKE_ICON_CLICKED, {
                  thoughtId,
                  liked: !liked,
                });
                handleLike();
              }}
              disabled={!thoughtId}
              aria-label={liked ? 'Unlike' : 'Like'}
            >
              {liked ? (
                <FavoriteIcon sx={{ color: '#FED892' }} />
              ) : <FavoriteBorderIcon sx={{ color: '#FED892' }} />}
            </IconButton>
            <Typography sx={{ color: '#fff', fontWeight: 400, fontSize: 18 }}>
              {likeCount.toLocaleString()}
            </Typography>
            <Box position='relative' display='inline-block'>
              <IconButton
                onClick={() => {
                  trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_SHARE_ICON_CLICKED, {
                    thoughtId,
                  });
                  handleCopy();
                }}
                sx={{ color: '#FED892', ml: 1 }}
                aria-label='Copy link'
              >
                <ShareIcon />
              </IconButton>
              {copied && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -32,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#333',
                  color: '#fff',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
              >
                Link copied!
              </Box>
              )}
            </Box>
            <Typography sx={{ color: '#fff', fontWeight: 400, fontSize: 18 }}>
              {shareCount.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box alignItems='center' display='flex' flexDirection='column' gap={1.5} mt={6} mb={3}>
        <SYLButton
          variant='contained'
          onClick={() => {
            trackMixpanelEvent(
              MixpanelEventName.SHAREYOURLIGHT_THOUGHT_SHARE_YOUR_LIGHT_BUTTON_CLICKED,
              {
                thoughtId,
              },
            );
            if (setAddMode) setAddMode(true);
            onClose();
          }}
          sx={{ maxWidth: 250, width: '100%' }}
        >
          SHARE YOUR LIGHT
        </SYLButton>
      </Box>
    </CommonModal>
  );
}

export default ThoughtViewModal;
